import type { NavigateFunction } from "react-router-dom";
import { getFetcher } from "api/swr";
import type { SpineAvatar } from "AdminPanel/SpineAvatars/types";
import {
  processTemplate,
  processStaticLayerTemplate,
} from "../editor/utils/templateLoader";
import { isStaticSpineTemplateType } from "../types/spine-template";
import type {
  ImportedOutfitConfig,
  ImportedTemplateData,
} from "../editor/import/types";
import { useConfigStore } from "../store/configStore";
import { useEditorStore } from "../store/editorStore";

/** React Router `location.state` 上挂待应用搭配（与编辑器消费方约定） */
export type SpineEditorLocationState = {
  pendingOutfitDrawData?: ImportedOutfitConfig;
};

export function parseDrawDataAsImportedOutfit(
  drawData: string | undefined | null,
): ImportedOutfitConfig | null {
  if (!drawData?.trim()) return null;
  try {
    return JSON.parse(drawData) as ImportedOutfitConfig;
  } catch {
    return null;
  }
}

function normalizeTemplateDataList(
  outfit: ImportedOutfitConfig,
  fallbackMainTemplateId: string,
): ImportedTemplateData[] {
  if (outfit.data && Array.isArray(outfit.data)) {
    return outfit.data;
  }
  if (outfit.skus && Array.isArray(outfit.skus)) {
    return [
      {
        templateId: fallbackMainTemplateId,
        skus: outfit.skus,
        tonings: outfit.tonings,
        animation: outfit.animation,
      },
    ];
  }
  return [];
}

/**
 * 将搭配写入编辑器 store（与 OutfitUploader 导入文件逻辑一致）
 */
export function applyImportedOutfitToStores(
  outfit: ImportedOutfitConfig,
  fallbackMainTemplateId?: string,
): { ok: true } | { ok: false; message: string } {
  const { templateConfig, currentTid, loadedRelatedTemplates } =
    useConfigStore.getState();
  const { setSelectedSkins, setSelectedToningColors, setCurrentAnimation, setPreviewToningId } =
    useEditorStore.getState();

  const fallback =
    fallbackMainTemplateId ||
    (templateConfig as { id?: string } | null)?.id ||
    currentTid ||
    "";

  const templateDataList = normalizeTemplateDataList(outfit, fallback);
  if (templateDataList.length === 0) {
    return { ok: false, message: "无效的搭配数据" };
  }

  if (!templateConfig) {
    return { ok: false, message: "请先加载模版配置" };
  }

  const allSelectedSkins: string[] = [];
  const allTonings: Record<string, string> = {};
  let targetAnimation: string | undefined;

  for (const tData of templateDataList) {
    let targetConfig = null;
    if (
      tData.templateId === currentTid ||
      tData.templateId === (templateConfig as { id?: string })?.id
    ) {
      targetConfig = templateConfig;
    } else {
      targetConfig = loadedRelatedTemplates.find(
        (r) => r.id === tData.templateId,
      )?.config;
    }

    if (!targetConfig) {
      console.warn(
        `[applyOutfit] 未找到模板 ID: ${tData.templateId}，跳过该部分数据`,
      );
      continue;
    }

    const skuIdToSkinName = new Map<string, string>();
    const skus = targetConfig.skus || {};
    for (const [skuId, sku] of Object.entries(skus)) {
      if ((sku as { skinName?: string }).skinName) {
        skuIdToSkinName.set(skuId, (sku as { skinName: string }).skinName);
      }
    }

    for (const skuId of tData.skus) {
      const skinName = skuIdToSkinName.get(skuId);
      if (skinName) {
        allSelectedSkins.push(skinName);
      } else {
        console.warn(
          `[applyOutfit] 模板 ${tData.templateId} 未找到对应 SKU: ${skuId}`,
        );
      }
    }

    if (tData.tonings) {
      Object.assign(allTonings, tData.tonings);
    }

    if (tData.animation && !targetAnimation) {
      targetAnimation = tData.animation;
    }
  }

  setSelectedSkins(allSelectedSkins);
  setSelectedToningColors(allTonings);
  setPreviewToningId(Object.keys(allTonings)[0] || null);
  if (targetAnimation) {
    setCurrentAnimation(targetAnimation);
  }

  return { ok: true };
}

function collectRelatedTemplateIdsToLoad(
  outfit: ImportedOutfitConfig,
  mainTid: string,
): string[] {
  const list = normalizeTemplateDataList(outfit, mainTid);
  const ids = new Set<string>();
  for (const t of list) {
    if (t.templateId && t.templateId !== mainTid) {
      ids.add(t.templateId);
    }
  }
  return [...ids];
}

/**
 * 按搭配里出现的 templateId 拉取尚未在 store 中的关联模版（与预览「导入搭配」一致）
 */
export async function ensureRelatedTemplatesForOutfit(
  outfit: ImportedOutfitConfig,
  mainTid: string,
  onProgress?: (msg: string) => void,
): Promise<void> {
  const needIds = collectRelatedTemplateIdsToLoad(outfit, mainTid);
  if (needIds.length === 0) return;

  const { loadedRelatedTemplates, addLoadedRelatedTemplate } =
    useConfigStore.getState();
  const loadedIds = new Set((loadedRelatedTemplates || []).map((r) => r.id));

  for (const relId of needIds) {
    if (loadedIds.has(relId)) continue;

    onProgress?.(`正在加载关联模版 ${relId}…`);
    const template = await getFetcher(`/spine-templates/${relId}`);

    if (!template.spineZipUrl) {
      if (isStaticSpineTemplateType(template.type)) {
        const processed = await processStaticLayerTemplate(
          relId,
          template,
          (m) => onProgress?.(m),
        );
        addLoadedRelatedTemplate({
          id: relId,
          type: template.type,
          config: processed.templateConfig,
          spineData: null,
          parsedSkins: [],
          spineVersion: template.spineVersion,
        });
      } else {
        console.warn(
          `[ensureRelated] 模板 ${relId} 无 ZIP 且非静态图层，跳过`,
        );
        continue;
      }
    } else {
      const processed = await processTemplate(relId, template, (m) =>
        onProgress?.(m),
      );
      addLoadedRelatedTemplate({
        id: relId,
        type: template.type,
        config: processed.templateConfig,
        spineData: processed.spineData,
        parsedSkins: processed.parsedSkins,
        spineVersion: template.spineVersion,
      });
    }
    loadedIds.add(relId);
  }
}

export function resolveMainTemplateIdForAvatar(
  avatar: SpineAvatar,
  tid?: string,
): string | null {
  const fromOutfit = (): string | null => {
    const o = parseDrawDataAsImportedOutfit(avatar.drawData);
    if (!o) return null;
    const list = normalizeTemplateDataList(
      o,
      tid || avatar.tid || "",
    );
    return list[0]?.templateId || null;
  };

  return avatar.tid || tid || fromOutfit() || null;
}

/**
 * 从 Avatar 菜单：跳转编辑器并带上待应用搭配，或已在目标编辑器时直接应用。
 */
export async function openAvatarDrawDataInEditor(options: {
  avatar: SpineAvatar;
  /** 列表筛选 tid 或编辑器当前模版 */
  tid?: string;
  /** useMatch('/admin/spine/editor/:tid')?.params?.tid */
  currentEditorTid?: string | null;
  navigate: NavigateFunction;
  onError: (message: string) => void;
  onStart?: () => void;
  onDone?: () => void;
}): Promise<void> {
  const {
    avatar,
    tid,
    currentEditorTid,
    navigate,
    onError,
    onStart,
    onDone,
  } = options;

  if (!avatar.drawData?.trim()) {
    onError("drawData 为空");
    return;
  }

  const outfit = parseDrawDataAsImportedOutfit(avatar.drawData);
  if (!outfit) {
    onError("drawData 格式无效");
    return;
  }

  const mainTid = resolveMainTemplateIdForAvatar(avatar, tid);
  if (!mainTid) {
    onError("无法确定模版 ID（缺少 tid 且 drawData 中无 templateId）");
    return;
  }

  const onSameEditor =
    currentEditorTid != null && currentEditorTid === mainTid;

  onStart?.();

  try {
    if (onSameEditor) {
      await ensureRelatedTemplatesForOutfit(outfit, mainTid);
      const result = applyImportedOutfitToStores(outfit, mainTid);
      if (result.ok === false) {
        onError(result.message);
        return;
      }
      onDone?.();
      return;
    }

    navigate(`/admin/spine/editor/${mainTid}`, {
      state: { pendingOutfitDrawData: outfit } satisfies SpineEditorLocationState,
    });
    onDone?.();
  } catch (e) {
    onError(e instanceof Error ? e.message : "操作失败");
  }
}
